import { constructionConstants, deliveryPackageConstants } from "@constants";
import { constructionService, deliveryPackageService } from "@services";
import { constructionActions, maskActions } from "@actions";
import { householdUtil, msgTypes, showModal } from "@utils";
import { find } from "lodash";

export const deliveryPackageActions = {
  /**
   * 取得貨運包裹列表
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} condition - 查詢條件
   * @param {function} cbSuccess - 成功後的 callback
   * @param {function} cbError - 失敗後的 callback
   * @param {object} options - 選項
   * @returns
   */
  getAll: (constructionUuid, condition, cbSuccess, cbError, options) => {
    return dispatch => {
      if (options?.loading) {
        dispatch(maskActions.toggleMask(true));
      }

      dispatch({
        type: deliveryPackageConstants.GET_ALL_REQUEST
      });

      deliveryPackageService
        .getAll(constructionUuid, condition)
        .then(
          ({ items, total }) => {
            dispatch({
              type: deliveryPackageConstants.GET_ALL_SUCCESS,
              constructionUuid,
              items,
              total,
              condition
            });

            cbSuccess && cbSuccess(items);
          },
          error => {
            cbError && cbError(error);
            dispatch({
              type: deliveryPackageConstants.GET_ALL_FAILURE,
              error
            });
          }
        )
        .finally(() => {
          if (options?.loading) {
            dispatch(maskActions.toggleMask(false));
          }
        });
    };
  },
  /**
   * 重置貨運包裹列表
   * @returns
   */
  resetAll: () => ({
    type: deliveryPackageConstants.RESET_ALL_SUCCESS
  }),
  /**
   * 新增貨運包裹資訊
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 貨運包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  create: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .create(constructionUuid, info)
        .then(
          res => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            const transferdHouseholdList = householdUtil.transferHouseholdList(res.data.households);
            dispatch({
              type: constructionConstants.GET_HOUSEHOLD_META_SUCCESS,
              householdMeta: transferdHouseholdList
            });
            cbSuccess && cbSuccess(res);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 取得貨運包裹資訊
   * @param {number} id - 貨運包裹 id
   * @param {function} cbSuccess - 成功後的 callback
   * @param {function} cbError - 失敗後的 callback
   * @returns
   */
  get: (id, cbSuccess, cbError) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .get(id)
        .then(
          deliveryPackage => {
            /**
             * 2023-05-29
             * 後端將原貨運包裹的狀態改成 history 的陣列格式，但前端畫面呈現未調整，故將後端資料轉換成前端原本的格式。
             **/

            const { history, status } = deliveryPackage;

            let currentHistoryStatus = null;
            if (status === "wait_collect") {
              currentHistoryStatus = find(history, { status: "wait_collect" });
            } else if (status === "wait_return") {
              currentHistoryStatus = find(history, { status: "wait_return" });
            }

            const converedDeliveryPackage = {
              ...deliveryPackage,
              // 將 History 陣列的狀態資料轉換成原本的格式
              ...(currentHistoryStatus
                ? {
                    files: currentHistoryStatus.files,
                    note: currentHistoryStatus.note,
                    status_created_at: currentHistoryStatus.created_at
                  }
                : {})
            };

            cbSuccess && cbSuccess(converedDeliveryPackage);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error, cbError);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 更新貨運包裹資訊
   * @param {number} id - 貨運包裹 id
   * @param {object} info - 貨運包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  update: (id, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .update(id, info)
        .then(
          response => {
            showModal(msgTypes.SUCCESS, "通知", response, cbSuccess);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 刪除貨運包裹
   * @param {number} id - 包裹 id
   * @param {string} constructionUuid - 建案 uuid
   * @returns
   */
  delete: (id, constructionUuid) => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .delete(id)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              deliveryPackageActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 取消待退貨包裹
   * @param {number} id - 包裹 id
   * @param {string} constructionUuid - 建案 uuid
   * @returns
   */
  cancelWaitReturn: (id, constructionUuid) => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .cancelWaitReturn(constructionUuid, id)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              deliveryPackageActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  __getAll: (dispatch, getState) => {
    const { deliveryPackages } = getState();
    dispatch(
      deliveryPackageActions.getAll(
        deliveryPackages?.query?.constructionUuid,
        deliveryPackages?.query?.condition
      )
    );
  },
  /**
   * 取得包裹類型選項
   * @returns
   */
  getTypeOptions: () => {
    return dispatch => {
      dispatch({
        type: deliveryPackageConstants.GET_TYPE_OPTIONS_REQUEST
      });

      constructionService.getOperateOptionList("delivery_package_type").then(
        typeOptions => {
          dispatch({
            type: deliveryPackageConstants.GET_TYPE_OPTIONS_SUCCESS,
            typeOptions: typeOptions.map(option => ({
              ...option,
              key: option.id,
              value: option.id,
              optionValue: option.value
            }))
          });
        },
        error => {
          dispatch({
            type: deliveryPackageConstants.GET_TYPE_OPTIONS_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重置包裹類型選項
   * @returns
   */
  resetTypeOptions: () => ({
    type: deliveryPackageConstants.RESET_TYPE_OPTIONS_SUCCESS
  }),
  /**
   * 取得包裹貨運類型選項
   * @returns
   */
  getDeliverByOptions: () => {
    return dispatch => {
      dispatch({
        type: deliveryPackageConstants.GET_DELIVER_BY_OPTIONS_REQUEST
      });

      constructionService.getOperateOptionList("delivery_package_deliver_by").then(
        deliverByOptions => {
          dispatch({
            type: deliveryPackageConstants.GET_DELIVER_BY_OPTIONS_SUCCESS,
            deliverByOptions: deliverByOptions.map(option => ({
              ...option,
              key: option.id,
              value: option.id
            }))
          });
        },
        error => {
          dispatch({
            type: deliveryPackageConstants.GET_DELIVER_BY_OPTIONS_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重置包裹貨運類型選項
   * @returns
   */
  resetDeliverByOptions: () => ({
    type: deliveryPackageConstants.RESET_DELIVER_BY_OPTIONS_SUCCESS
  }),
  /**
   * 取得包裹保存選項
   * @returns
   */
  getPreserveOptions: () => {
    return dispatch => {
      dispatch({
        type: deliveryPackageConstants.GET_PRESERVE_OPTIONS_REQUEST
      });

      constructionService.getOperateOptionList("delivery_package_preserve").then(
        preserveOptions => {
          dispatch({
            type: deliveryPackageConstants.GET_PRESERVE_OPTIONS_SUCCESS,
            preserveOptions: preserveOptions.map(option => ({
              ...option,
              key: option.id,
              value: option.id
            }))
          });
        },
        error => {
          dispatch({
            type: deliveryPackageConstants.GET_PRESERVE_OPTIONS_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重置包裹保存選項
   * @returns
   */
  resetPreserveOptions: () => ({
    type: deliveryPackageConstants.RESET_PRESERVE_OPTIONS_SUCCESS
  }),
  /**
   * 領取貨運包裹
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 包裹資訊
   * @returns
   */
  collect: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .collect(constructionUuid, info)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 待退貨貨運包裹登記
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  return: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .return(constructionUuid, info)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 退貨貨運包裹完成
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  returned: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      deliveryPackageService
        .returned(constructionUuid, info)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  }
};
